import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import MobileMenu from './MobileMenu'
import DeskMenu from './DeskMenu'
import { useLocation } from '@reach/router'
import { useRecoilValue } from 'recoil'
import { language } from '../../types'
import { langAtom } from '../../atoms'
import { urls } from '../../consts/urls'
import { langFromPath, localizedPath, pathWithTrailingSlash } from '../../utils/language'

const headerList = [
  {
    title: { JA: 'ホーム', EN: 'Home', ZH: '首页', TW: '首頁', KO: '홈' },
    link: '/',
  },
  {
    title: { JA: 'へんこつについて', EN: 'About', ZH: '关于我们', TW: '關於我們', KO: '소개' },
    link: '/about/',
  },
  {
    title: { JA: 'メニュー', EN: 'Menu', ZH: '菜单', TW: '菜單', KO: '메뉴' },
    link: '/menus/',
  },
  {
    title: { JA: '店舗一覧', EN: 'Locations', ZH: '门店一览', TW: '分店一覽', KO: '매장 안내' },
    link: '/branch/',
  },
  {
    title: { JA: 'テイクアウトご注文', EN: 'Takeout Order', ZH: '外带订购', TW: '外帶訂購', KO: '테이크아웃 주문' },
    link: urls.takeawayOrder,
    external: true,
  },
]

export default function Header() {
  const location = useLocation()
  const path = location.pathname
  const isRoot = path === '/' || path === '/en/' || path === '/zh/' || path === '/tw/' || path === '/ko/'
  const [isScrolled, setIsScrolled] = useState(false)
  const recoilLang = useRecoilValue<language>(langAtom)
  const lang = langFromPath(path) || recoilLang
  const pathWithoutLocale = path.replace(/^\/(en|zh|tw|ko)(?=\/|$)/, '') || '/'
  const localizedPagePaths = new Set([
    '/',
    '/about/',
    '/menus/',
    '/branch/',
    '/contact/',
    '/companyInfo/',
  ])
  const showFlags = localizedPagePaths.has(pathWithTrailingSlash(pathWithoutLocale))

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    setIsScrolled(scrollTop >= 300)
  }, [])

  useEffect(() => {
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null
    const debouncedHandleScroll = () => {
      if (debounceTimeout !== null) {
        clearTimeout(debounceTimeout)
      }
      debounceTimeout = setTimeout(handleScroll, 50)
    }
    window.addEventListener('scroll', debouncedHandleScroll)
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll)
      if (debounceTimeout !== null) {
        clearTimeout(debounceTimeout)
      }
    }
  }, [handleScroll, isRoot])

  const changeLangLink = (toLang: language) => {
    const basePath = location.pathname.replace(/^\/(en|zh|tw|ko)/, '')
    return localizedPath(toLang, basePath)
  }

  return (
    <header
      className={`fixed lg:absolute w-full max-w-[1600px] z-50 transition-colors duration-300 ${isRoot && !isScrolled ? 'bg-transparent lg:bg-black/45 lg:backdrop-blur-sm' : 'bg-white border-b'
        }`}
    >
      <nav className='flex justify-between items-center gap-4 py-4 px-2 sm:py-6 sm:px-4 lg:gap-4 xl:gap-6 xl:px-8 xl:py-8 2xl:px-16 2xl:py-10'>
        <Link to={localizedPath(lang)} className='shrink-0'>
          <StaticImage
            src='../../images/logos/logo.webp'
            alt='Henkotsu'
            className='w-32 mx-auto sm:w-40 lg:w-32 xl:w-48 2xl:w-60'
            placeholder='blurred'
            layout='constrained'
            loading='eager'
            quality={90}
          />

        </Link>
        <MobileMenu
          isRoot={isRoot}
          isScrolled={isScrolled}
          headers={headerList}
          lang={lang}
          changeLangLink={changeLangLink}
          showFlags={showFlags}
        />
        <DeskMenu
          isRoot={isRoot}
          headers={headerList}
          lang={lang}
          changeLangLink={changeLangLink}
          path={path}
          showFlags={showFlags}
        />
      </nav>
    </header>
  )
}
